import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { ApiService } from '../services/api';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  // Handle SSO token from URL (Moodle redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const ssoToken = params.get('sso_token');
    const error = params.get('error');
    const message = params.get('message');

    // Handle direct JWT token (from SSO redirect)
    if (token) {
      setSsoLoading(true);
      handleSSOToken(token);
    }
    
    // Handle SSO token that needs validation (alternative flow)
    if (ssoToken) {
      setSsoLoading(true);
      // Redirect to SSO endpoint to validate and get JWT
      window.location.href = `${import.meta.env.VITE_API_URL || ''}/api/auth/sso?token=${ssoToken}`;
    }

    if (error) {
      // SSO login failed
      const errorMessage = message ? decodeURIComponent(message.replace(/\+/g, ' ')) : 'Login failed';
      toast.error(errorMessage);
      // Clean URL
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleSSOToken = async (token: string) => {
    try {
      // Store token in localStorage and ApiService
      localStorage.setItem('token', token);
      ApiService.setToken(token);
      
      // Clean URL
      window.history.replaceState({}, '', '/login');
      
      // Reload the page to trigger auth check
      // This will cause useAuth to re-fetch the user
      window.location.href = '/';
    } catch (error: any) {
      console.error('SSO token validation failed:', error);
      toast.error('Session expired. Please login again.');
      localStorage.removeItem('token');
      ApiService.setToken(null);
      window.history.replaceState({}, '', '/login');
      setSsoLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMoodleLogin = () => {
    window.location.href = ApiService.getMoodleLoginUrl();
  };

  // Show loading screen while processing SSO token
  if (ssoLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <img src="/iiitdlogo.jpeg" alt="iiitd University" className="h-10 object-contain" />
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Signing you in...</h2>
            <p className="text-slate-500 text-sm">Please wait while we verify your session</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* White Header with Logo */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <img src="/iiitdlogo.jpeg" alt="IIIT Dharwad" className="h-10 object-contain" />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Login Card */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
            <h1 className="text-xl font-semibold text-slate-800 text-center mb-1">Reflection Journal</h1>
            <p className="text-slate-500 text-sm text-center mb-6">Sign in to your account</p>

            <button 
              onClick={handleMoodleLogin} 
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              Sign in with Moodle
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center"><span className="px-3 bg-white text-slate-400 text-xs uppercase">or</span></div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" 
                  placeholder="you@example.com" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" 
                  placeholder="••••••••" 
                  required 
                />
              </div>
              <button 
                type="submit" 
                disabled={loading} 
                className="w-full px-4 py-2.5 bg-[#1e3a5f] hover:bg-[#152a45] disabled:bg-slate-300 text-white font-medium rounded-md transition-colors text-sm"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="text-center text-xs text-slate-400 mt-6">
              Contact your administrator for access
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-slate-400">
        © 2025 IIIT Dharwad. All rights reserved.
      </footer>
    </div>
  );
};
